import { useState, useCallback, useEffect } from 'react';
import { useRevenueCat } from '../contexts/RevenueCatContext';

interface UseSubscriptionReturn {
  // Subscription status
  isActive: boolean;
  isPremium: boolean;
  isPro: boolean;
  loading: boolean;
  error: string | null;
  isInitialized: boolean;
  
  // Actions
  purchase: (productId: string) => Promise<void>;
  restore: () => Promise<void>;
  refresh: () => Promise<void>;
  
  // Helpers
  hasFeature: (feature: string) => boolean;
}

/**
 * Custom hook for managing subscriptions
 * Provides simplified access to RevenueCat functionality
 */
export const useSubscription = (): UseSubscriptionReturn => {
  const {
    isInitialized,
    subscriptionStatus,
    loading,
    error,
    purchaseProduct,
    restorePurchases,
    refreshSubscriptionStatus,
  } = useRevenueCat();

  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Computed subscription status
  const isActive = subscriptionStatus?.isActive ?? false;
  const isPremium = subscriptionStatus?.entitlements.includes('premium') ?? false;
  const isPro = subscriptionStatus?.entitlements.includes('thevisaai_pro') ?? false;

  // Enhanced debug logging
  console.log('🔍 Subscription Hook Debug:');
  console.log('🔍 isInitialized:', isInitialized);
  console.log('🔍 loading:', loading);
  console.log('🔍 subscriptionStatus:', subscriptionStatus);
  console.log('🔍 isActive:', isActive);
  console.log('🔍 entitlements array:', subscriptionStatus?.entitlements);
  console.log('🔍 isPremium:', isPremium);
  console.log('🔍 isPro:', isPro);
  console.log('🔍 error:', error);

  // Auto-retry mechanism: If initialized and not loading, but subscription status seems incomplete
  useEffect(() => {
    if (!isInitialized || loading || isRefreshing) return;
    
    // If we have subscription status but it shows no entitlements, wait a bit then try refresh
    // This handles cases where initial load doesn't detect existing subscriptions
    if (subscriptionStatus && !subscriptionStatus.isActive) {
      console.log('🤔 Subscription initialized but no active entitlements found. Scheduling auto-refresh...');
      
      const autoRefreshTimer = setTimeout(async () => {
        try {
          console.log('🔄 Auto-refreshing subscription status...');
          setIsRefreshing(true);
          await refreshSubscriptionStatus();
          console.log('✅ Auto-refresh completed');
        } catch (err) {
          console.error('❌ Auto-refresh failed:', err);
        } finally {
          setIsRefreshing(false);
        }
      }, 3000); // Wait 3 seconds after initialization
      
      return () => clearTimeout(autoRefreshTimer);
    }
  }, [isInitialized, loading, subscriptionStatus, refreshSubscriptionStatus, isRefreshing]);

  // Purchase a product
  const purchase = useCallback(async (productId: string): Promise<void> => {
    try {
      setIsPurchasing(true);
      const result = await purchaseProduct(productId);
      
      if (!result.success) {
        throw new Error(result.error || 'Purchase failed');
      }
    } catch (err) {
      console.error('Purchase failed:', err);
      throw err;
    } finally {
      setIsPurchasing(false);
    }
  }, [purchaseProduct]);

  // Restore purchases
  const restore = useCallback(async (): Promise<void> => {
    try {
      setIsRestoring(true);
      const result = await restorePurchases();
      
      if (!result.success) {
        throw new Error(result.error || 'Restore failed');
      }
    } catch (err) {
      console.error('Restore failed:', err);
      throw err;
    } finally {
      setIsRestoring(false);
    }
  }, [restorePurchases]);

  // Refresh subscription status with enhanced logging
  const refresh = useCallback(async (): Promise<void> => {
    try {
      console.log('🔄 Manual refresh triggered from useSubscription hook');
      setIsRefreshing(true);
      await refreshSubscriptionStatus();
    } catch (err) {
      console.error('Refresh failed:', err);
      throw err;
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshSubscriptionStatus]);

  // Check if user has access to specific features
  const hasFeature = useCallback((feature: string): boolean => {
    switch (feature) {
      case 'unlimited_chats':
        return isPro;
      case 'priority_support':
        return isPro;
      case 'advanced_analytics':
        return isPro;
      case 'custom_templates':
        return isPro;
      case 'document_upload':
        return isPro;
      case 'profile_analysis':
        return isPro;
      case 'personal_statement_generation':
        return isPro;
      case 'export_documents':
        return isPro;
      case 'chat_history':
        return isPro;
      case 'unlimited_profile_analyses':
        return isPro;
      default:
        return false;
    }
  }, [isPro]);

  return {
    // Status
    isActive,
    isPremium,
    isPro,
    loading: loading || isPurchasing || isRestoring || isRefreshing,
    error,
    isInitialized,
    
    // Actions
    purchase,
    restore,
    refresh,
    
    // Helpers
    hasFeature,
  };
};

export default useSubscription; 